import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";

/**
 * Empresas — página B2B minimalista.
 *
 * Misma atmósfera que LandingV2:
 *   - Negro premium #050505
 *   - Capa atmosférica (glows teal/azul difuminados, grain sutil)
 *   - Tipografía protagonista
 *
 * Estructura:
 *   1. Nav (consistente con LandingV2)
 *   2. Hero — promesa B2B
 *   3. Formato — cómo se implementa
 *   4. Casos — equipos donde funciona
 *   5. Contacto — CTA único y claro
 *   6. Footer compacto
 */

// Reusable reveal hook (mismo patrón que LandingV2)
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, visible];
}

export default function EmpresasPage() {
  return (
    <div
      data-testid="empresas-page"
      className="bg-[#050505] text-white min-h-screen relative overflow-x-hidden"
    >
      {/* Global grain overlay */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[1] opacity-[0.035]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.85) 1px, transparent 0)",
          backgroundSize: "3px 3px",
        }}
      />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/30 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link
            to="/"
            className="text-sm font-semibold tracking-[0.32em] hover:opacity-80 transition-opacity"
          >
            PLAY YOUR GAME™
          </Link>
          <div className="hidden md:flex items-center gap-8 text-[10px] uppercase tracking-[0.3em] font-medium text-white/45">
            <Link to="/" className="hover:text-white transition-colors duration-300">
              Volver al inicio
            </Link>
          </div>
          <a
            href="mailto:hola@playyourgame.com?subject=Programa%20para%20mi%20equipo"
            data-testid="nav-contact-btn"
            className="inline-flex items-center gap-2 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[10px] tracking-[0.22em] uppercase font-bold px-4 py-2.5 rounded-sm transition-all duration-300"
          >
            Contactar
            <ArrowRight size={11} />
          </a>
        </div>
      </nav>

      {/* ───── Hero ───── */}
      <HeroEmpresas />

      {/* ───── Formato ───── */}
      <FormatoSection />

      {/* ───── Casos ───── */}
      <CasosSection />

      {/* ───── Contacto ───── */}
      <ContactoSection />

      {/* ───── Footer ───── */}
      <FooterCompact />
    </div>
  );
}

// ───────────────────────── HERO ───────────────────────── //
function HeroEmpresas() {
  return (
    <section
      data-testid="empresas-hero"
      className="relative min-h-[88vh] flex items-center px-6 pt-32 pb-20 overflow-hidden"
    >
      {/* Atmospheric layers */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/4 left-[-15%] w-[1000px] h-[1000px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(56,90,140,0.18) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-20%] right-[-10%] w-[800px] h-[800px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(41,188,155,0.10) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />

      <div className="relative w-full max-w-[1180px] mx-auto">
        <div className="text-[11px] tracking-[0.4em] uppercase text-[#29BC9B] font-semibold mb-12">
          Empresas · Programa estratégico
        </div>

        <h1
          data-testid="empresas-hero-title"
          className="font-semibold leading-[1] tracking-[-0.025em] text-[44px] sm:text-[64px] md:text-[84px] lg:text-[100px] max-w-[1080px]"
        >
          Tu equipo no necesita
          <br />
          <span className="text-white/45">otra capacitación.</span>
          <br />
          <span className="relative inline-block">
            <span className="relative z-[1]">Necesita claridad.</span>
            <span
              aria-hidden
              className="absolute inset-x-0 bottom-2 h-[14px] bg-[#29BC9B]/25 -z-0"
            />
          </span>
        </h1>

        <p className="mt-14 text-[17px] sm:text-[19px] leading-relaxed text-white/55 max-w-[640px]">
          Play Your Game™ se implementa en empresas que buscan una conversación
          distinta sobre cómo juegan sus líderes bajo presión. Diagnóstico,
          programa de evolución por rol y coach estratégico a escala.
        </p>

        <div className="mt-16 flex flex-col sm:flex-row gap-4">
          <a
            href="#contacto"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center justify-center gap-3 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[12px] tracking-[0.22em] uppercase font-bold px-7 py-4 rounded-sm transition-all duration-500"
          >
            Agendar una conversación
            <ArrowRight
              size={14}
              className="group-hover:translate-x-1 transition-transform"
            />
          </a>
          <a
            href="#formato"
            className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/40 text-white/70 hover:text-white text-[12px] tracking-[0.22em] uppercase font-semibold px-7 py-4 rounded-sm transition-all duration-500"
          >
            Cómo funciona
          </a>
        </div>
      </div>
    </section>
  );
}

// ───────────────────────── FORMATO ───────────────────────── //
function FormatoSection() {
  const [ref, visible] = useReveal(0.15);
  const steps = [
    {
      n: "01",
      title: "Diagnóstico",
      copy: "Tu equipo responde el test PYG™. Recibimos su mapa estratégico: cómo juegan, cuál es su modo sombra, dónde tropiezan bajo presión.",
      duration: "Semana 1",
    },
    {
      n: "02",
      title: "Lectura por unidad",
      copy: "Workshop ejecutivo de 90 minutos con los líderes. Lectura de la cultura del equipo, patrones por rol, oportunidades por avatar.",
      duration: "Semana 2",
    },
    {
      n: "03",
      title: "Programa de evolución",
      copy: "Cada colaborador accede a su Mapa Completo, Coach Estratégico (IA) y rutas de evolución por rol. Acompañamiento humano selectivo.",
      duration: "Semanas 3 a 12",
    },
    {
      n: "04",
      title: "Métricas y retorno",
      copy: "Dashboard por unidad. Comparativos antes/después en madurez bajo presión, claridad de decisión y trabajo en equipo.",
      duration: "Mes 4 en adelante",
    },
  ];

  return (
    <section
      ref={ref}
      id="formato"
      data-testid="formato-section"
      className="relative px-6 py-32 md:py-40 border-t border-white/[0.05]"
      style={{
        background:
          "linear-gradient(180deg, #050505 0%, #0A0E13 50%, #050505 100%)",
      }}
    >
      <div className="relative w-full max-w-[1180px] mx-auto">
        <div
          className={`text-[11px] tracking-[0.4em] uppercase text-[#29BC9B] font-semibold mb-10 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          Formato del programa
        </div>

        <h2
          className={`font-semibold leading-[1.05] tracking-[-0.025em] text-[36px] sm:text-[52px] md:text-[64px] max-w-[900px] transition-all duration-1000 delay-150 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          Cuatro fases.
          <br />
          <span className="text-white/55">Un sistema estratégico claro.</span>
        </h2>

        <div className="mt-20 space-y-2">
          {steps.map((s, i) => (
            <div
              key={s.n}
              data-testid={`formato-step-${i + 1}`}
              className="group relative grid grid-cols-12 gap-6 items-start border-t border-white/[0.06] py-10 hover:bg-white/[0.015] transition-colors duration-500"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 1000ms, transform 1000ms",
                transitionDelay: `${350 + i * 120}ms`,
              }}
            >
              <div className="col-span-12 md:col-span-2">
                <div className="text-[10px] tracking-[0.32em] uppercase font-bold text-[#29BC9B]/70">
                  {s.n}
                </div>
                <div className="mt-2 text-[11px] tracking-[0.2em] uppercase text-white/35 font-medium">
                  {s.duration}
                </div>
              </div>
              <div className="col-span-12 md:col-span-4">
                <div className="text-[24px] sm:text-[28px] font-semibold tracking-[-0.015em] text-white group-hover:text-[#29BC9B] transition-colors duration-500">
                  {s.title}
                </div>
              </div>
              <div className="col-span-12 md:col-span-6">
                <p className="text-[15px] leading-relaxed text-white/55">
                  {s.copy}
                </p>
              </div>
            </div>
          ))}
          <div className="border-t border-white/[0.06]" />
        </div>
      </div>
    </section>
  );
}

// ───────────────────────── CASOS ───────────────────────── //
function CasosSection() {
  const [ref, visible] = useReveal(0.15);
  const useCases = [
    {
      label: "Equipos comerciales",
      copy: "Cuando la presión por número activa el modo sombra de cada vendedor. Saber cómo juega cada uno cambia cómo se asignan cuentas y se cierra negocio.",
    },
    {
      label: "Líderes en transición",
      copy: "Promociones a roles ejecutivos donde la madurez bajo presión define el salto. Mapas individuales + acompañamiento humano selectivo.",
    },
    {
      label: "Operaciones críticas",
      copy: "Industrias donde una mala decisión escala rápido. El equipo aprende a identificar y regular su modo sombra antes de que cueste.",
    },
  ];

  return (
    <section
      ref={ref}
      data-testid="casos-section"
      className="relative px-6 py-32 md:py-40 bg-[#F4F4F5] text-[#0A0A0A] overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-0 inset-x-0 h-px"
        style={{
          background:
            "linear-gradient(to right, transparent, rgba(41,188,155,0.30), transparent)",
        }}
      />

      <div className="relative w-full max-w-[1180px] mx-auto">
        <div
          className={`text-[11px] tracking-[0.4em] uppercase text-[#29BC9B] font-semibold mb-10 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          Dónde funciona mejor
        </div>

        <h2
          className={`font-semibold leading-[1.05] tracking-[-0.025em] text-[36px] sm:text-[52px] md:text-[64px] max-w-[900px] transition-all duration-1000 delay-150 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          Tres contextos donde
          <br />
          <span className="text-zinc-400">la claridad cambia el juego.</span>
        </h2>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {useCases.map((u, i) => (
            <div
              key={u.label}
              data-testid={`caso-${i + 1}`}
              className="bg-white border border-black/[0.06] rounded-lg p-9 hover:shadow-[0_12px_36px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-500"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 1000ms, transform 1000ms",
                transitionDelay: `${400 + i * 130}ms`,
              }}
            >
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold text-[#29BC9B] mb-6">
                Caso {String(i + 1).padStart(2, "0")}
              </div>
              <div className="text-[20px] font-semibold tracking-[-0.015em] text-[#0A0A0A] leading-tight">
                {u.label}
              </div>
              <p className="mt-5 text-[15px] leading-relaxed text-zinc-600">
                {u.copy}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div
        aria-hidden
        className="absolute bottom-0 inset-x-0 h-px"
        style={{
          background:
            "linear-gradient(to right, transparent, rgba(41,188,155,0.30), transparent)",
        }}
      />
    </section>
  );
}

// ───────────────────────── CONTACTO ───────────────────────── //
function ContactoSection() {
  const [ref, visible] = useReveal(0.25);

  return (
    <section
      ref={ref}
      id="contacto"
      data-testid="contacto-section"
      className="relative min-h-[80vh] flex items-center justify-center px-6 py-32 overflow-hidden bg-[#050505]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 50% 60%, rgba(41,188,155,0.18) 0%, transparent 60%)",
          filter: "blur(40px)",
        }}
      />

      <div className="relative w-full max-w-[920px] mx-auto text-center">
        <div
          className={`text-[11px] tracking-[0.4em] uppercase text-[#29BC9B] font-semibold mb-10 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          Empezar una conversación
        </div>

        <h2
          data-testid="contacto-title"
          className={`font-semibold leading-[1.02] tracking-[-0.025em] text-[38px] sm:text-[54px] md:text-[68px] transition-all duration-1000 delay-150 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          Te contamos cómo se adapta
          <br />
          <span className="text-white/45">a tu organización.</span>
        </h2>

        <p
          className={`mt-12 text-[16px] sm:text-[18px] leading-relaxed text-white/55 max-w-[600px] mx-auto transition-all duration-1000 delay-300 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          Una llamada de 30 minutos. Sin formularios largos, sin prospección
          fría. Solo entender si tiene sentido y, si sí, cómo se ve.
        </p>

        <div
          className={`mt-14 flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-1000 delay-500 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <a
            href="mailto:hola@playyourgame.com?subject=Programa%20para%20mi%20equipo&body=Hola%2C%20me%20gustar%C3%ADa%20conocer%20el%20programa%20de%20Play%20Your%20Game%20para%20mi%20equipo.%0A%0AEmpresa%3A%0AEquipo%2Frol%3A%0AN%C3%BAmero%20aproximado%20de%20colaboradores%3A"
            data-testid="contacto-cta-email"
            className="group inline-flex items-center gap-3 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[12px] tracking-[0.22em] uppercase font-bold px-8 py-4 rounded-sm transition-all duration-500 shadow-[0_8px_40px_-12px_rgba(41,188,155,0.6)]"
          >
            <Mail size={15} />
            hola@playyourgame.com
          </a>
        </div>

        <div
          className={`mt-10 text-[10px] tracking-[0.32em] uppercase text-white/30 font-semibold transition-all duration-1000 delay-700 ${
            visible ? "opacity-100" : "opacity-0"
          }`}
        >
          Respondemos en menos de 48 horas hábiles.
        </div>
      </div>
    </section>
  );
}

// ───────────────────────── FOOTER ───────────────────────── //
function FooterCompact() {
  return (
    <footer className="relative px-6 py-12 border-t border-white/[0.06] bg-[#050505]">
      <div className="max-w-[1180px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <Link
            to="/"
            className="text-sm font-semibold tracking-[0.32em] hover:opacity-80 transition-opacity"
          >
            PLAY YOUR GAME™
          </Link>
          <div className="mt-3 text-[10px] tracking-[0.32em] uppercase text-[#29BC9B]/70 font-semibold">
            Juega mejor bajo presión.
          </div>
        </div>
        <div className="flex items-center gap-8 text-[11px] tracking-[0.22em] uppercase text-white/30 font-medium">
          <Link to="/" className="hover:text-white/70 transition-colors">
            Inicio
          </Link>
          <Link to="/acceder" className="hover:text-white/70 transition-colors">
            Acceder
          </Link>
          <a
            href="mailto:hola@playyourgame.com"
            className="hover:text-white/70 transition-colors"
          >
            Contacto
          </a>
        </div>
      </div>
      <div className="max-w-[1180px] mx-auto mt-8 pt-6 border-t border-white/[0.05] text-[11px] text-white/30 tracking-wide">
        © {new Date().getFullYear()} Play Your Game™. Todos los derechos
        reservados.
      </div>
    </footer>
  );
}
